import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { logoutUser } from "../../services/auth/authorisation";

const NAV_ITEMS = [
  { text: "Dashboard", route: "/dashboard" },
  { text: "Tasks", route: "/tasks" },
  { text: "Logs", route: "/logs" },
  { text: "Export Json", route: "/export-json" },
  { text: "Auto4client", route: "/auto4client" },
  { text: "Admin", route: "/admin" },
  { text: "Data Uploader", route: "/data-uploader" },
  { text: "Geocoder", route: "/geocoder" },
  { text: "Sheet Generator", route: "/sheet-generator" },
  { text: "Classification Pipeline", route: "/classification-pipeline" },
  { text: "Preflight Checks", route: "/preflight-checks" },
];

// Screen width the inline nav links need to fit on one row (wider than MUI's xl breakpoint)
const INLINE_NAV_MIN_WIDTH = 1650;

/**
 * MenuBarItem component.
 *
 * This component renders a menu item button that navigates to the specified route when clicked.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.text - Text for the button label.
 * @param {string} props.route - Route to navigate to when the button is clicked.
 *
 * @returns {JSX.Element} - The JSX element for the menu item button.
 */
function MenuBarItem({ text, route }) {
  const navigate = useNavigate();

  return (
    <Button
      key={text}
      onClick={() => navigate(route)}
      sx={{ my: 2, color: "white", display: "block", whiteSpace: "nowrap", flexShrink: 0 }}
    >
      {text}
    </Button>
  );
}

/**
 * NavDrawer component.
 *
 * This component renders the navigation links in a side drawer, used when they don't fit inline.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {boolean} props.open - Whether the drawer is open.
 * @param {Function} props.onClose - Called when the drawer should close.
 *
 * @returns {JSX.Element} - The JSX element for the navigation drawer.
 */
function NavDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Drawer anchor="left" open={open} onClose={onClose}>
      <List sx={{ width: 250 }}>
        {NAV_ITEMS.map(({ text, route }) => (
          <ListItemButton
            key={text}
            selected={location.pathname === route}
            onClick={() => {
              onClose();
              navigate(route);
            }}
          >
            <ListItemText primary={text} />
          </ListItemButton>
        ))}
      </List>
    </Drawer>
  );
}

/**
 * MenuBar component.
 *
 * This component renders a navigation bar with an icon and menu items.
 * Below INLINE_NAV_MIN_WIDTH the menu items move into a drawer opened by the menu button.
 *
 * @returns {JSX.Element} - The JSX element for the menu bar.
 */
function MenuBar() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const showInlineNav = useMediaQuery(`(min-width:${INLINE_NAV_MIN_WIDTH}px)`, { noSsr: true });

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" color="secondary">
        <Toolbar>
          {!showInlineNav && (
            <IconButton
              size="large"
              edge="start"
              color="inherit"
              aria-label="open navigation menu"
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          )}

          <IconButton size="large" edge="start" color="inherit" aria-label="menu" sx={{ mr: 0 }}>
            <Box
              component="img"
              sx={{ height: "4rem", width: "100%" }}
              src="https://preview.redd.it/gsg71myc1s601.png?auto=webp&s=da664cd405b2f5c51fed90e3b26cebcee9ec82fa"
            />
          </IconButton>

          {/* Inline nav links, or an empty spacer keeping Settings / Log Out on the right */}
          <Box sx={{ flexGrow: 1, display: "flex" }}>
            {showInlineNav &&
              NAV_ITEMS.map(({ text, route }) => <MenuBarItem key={text} text={text} route={route} />)}
          </Box>

          <MenuBarItem text={"Settings"} route={"/settings"} />
          <Button
            color="inherit"
            onClick={async () => {
              await logoutUser();
              window.location.reload();
            }}
            sx={{ my: 2, color: "white", display: "block", whiteSpace: "nowrap", flexShrink: 0 }}
          >
            Log Out
          </Button>
        </Toolbar>
      </AppBar>

      <NavDrawer open={drawerOpen && !showInlineNav} onClose={() => setDrawerOpen(false)} />
    </Box>
  );
}

export default MenuBar;
